import React, { useEffect, useRef, useState } from 'react';
import {
  FolderOpenIcon,
  DocumentTextIcon,
  CalculatorIcon,
  ClipboardDocumentListIcon,
  PencilSquareIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

interface StepData {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  gradient: string;
  accentColor: string;
  lightBg: string;
  lightText: string;
  features: string[];
  tags: string[];
}

const STEPS: StepData[] = [
  {
    id: '01',
    name: '项目配置',
    description: '录入项目基本信息，选择投标主体与 AI 分析模型，上传招标文件，系统自动准备智能分析环境，为后续智能解析做好准备。',
    icon: FolderOpenIcon,
    gradient: 'linear-gradient(145deg, #1d4ed8 0%, #0369a1 100%)',
    accentColor: '#3b82f6',
    lightBg: '#eff6ff',
    lightText: '#1d4ed8',
    features: [
      '录入项目名称、类型与预期字数',
      '选择投标主体公司及法人信息',
      '配置 AI 模型与分析精度参数',
      '支持 Word / PDF 招标文件上传',
    ],
    tags: ['项目信息', '主体选择', '文件上传'],
  },
  {
    id: '02',
    name: '招标解析',
    description: 'AI 深度解析招标文件，六大维度全面提取关键信息，精准识别技术评分标准、目录格式要求与废标风险项，为标书创作奠定基础。',
    icon: DocumentTextIcon,
    gradient: 'linear-gradient(145deg, #0369a1 0%, #0e7490 100%)',
    accentColor: '#0ea5e9',
    lightBg: '#f0f9ff',
    lightText: '#0369a1',
    features: [
      'AI 提取项目概述与背景信息',
      '解析技术评分标准与权重分配',
      '识别目录结构与格式要求',
      '自动预警废标风险条款',
    ],
    tags: ['AI 解析', '评分标准', '风险预警'],
  },
  {
    id: '03',
    name: '分项报价',
    description: '智能识别招标文件中的报价要求，辅助填写分项报价明细，实时自动计算总价与成本占比，提供超价预警保护，确保报价合规。',
    icon: CalculatorIcon,
    gradient: 'linear-gradient(145deg, #0f766e 0%, #0369a1 100%)',
    accentColor: '#14b8a6',
    lightBg: '#f0fdfa',
    lightText: '#0f766e',
    features: [
      'AI 自动识别报价项目清单',
      '支持多项目分项报价填写',
      '实时自动计算总价与成本占比',
      '超过限额实时预警提醒',
    ],
    tags: ['智能报价', '自动计算', '风险控制'],
  },
  {
    id: '04',
    name: '目录生成',
    description: '基于招标解析结果，AI 自动生成符合招标要求的专业标书目录结构，支持三级章节体系，可拖拽排序与手动微调，完美匹配招标规范。',
    icon: ClipboardDocumentListIcon,
    gradient: 'linear-gradient(145deg, #1d4ed8 0%, #0e7490 100%)',
    accentColor: '#2563eb',
    lightBg: '#eff6ff',
    lightText: '#1d4ed8',
    features: [
      'AI 根据评标要求智能生成目录',
      '支持六级目录结构（章/节/条）',
      '拖拽排序自由调整章节位置',
      '支持添加、删除与重命名章节',
    ],
    tags: ['AI 生成', '三级目录', '拖拽排序'],
  },
  {
    id: '05',
    name: '文件生成',
    description: '按章节 AI 逐一生成完整标书正文内容，智能覆盖所有技术评分指标，支持实时编辑、段落优化与批注记录，最终一键导出专业 Word 文档。',
    icon: PencilSquareIcon,
    gradient: 'linear-gradient(145deg, #1e3a8a 0%, #1d4ed8 100%)',
    accentColor: '#3b82f6',
    lightBg: '#eff6ff',
    lightText: '#1e3a8a',
    features: [
      'AI 逐章节生成标书正文内容',
      '确保覆盖所有技术评分指标',
      '支持章节内容批注与修改',
      '一键导出专业 Word 文档',
    ],
    tags: ['内容生成', '指标覆盖', 'Word 导出'],
  },
];

const StepVisual: React.FC<{ step: StepData }> = ({ step }) => {
  const Icon = step.icon;
  return (
    <div
      className="relative w-full h-72 rounded-3xl flex items-center justify-center overflow-hidden flex-shrink-0"
      style={{
        background: step.gradient,
        boxShadow: `0 24px 64px ${step.accentColor}35`,
      }}
    >
      <span
        className="absolute font-black leading-none select-none pointer-events-none"
        style={{
          fontSize: '140px',
          color: 'rgba(255,255,255,0.07)',
          top: '-20px',
          right: '-10px',
        }}
      >
        {step.id}
      </span>

      <div className="absolute top-5 right-6 w-3.5 h-3.5 rounded-full bg-white/25" />
      <div className="absolute top-9 right-14 w-2 h-2 rounded-full bg-white/15" />
      <div className="absolute bottom-6 left-6 w-3 h-3 rounded-full bg-white/20" />
      <div className="absolute bottom-12 left-14 w-2 h-2 rounded-full bg-white/25" />
      <div className="absolute top-1/2 left-5 w-1.5 h-1.5 rounded-full bg-white/15" />

      <div className="relative z-10 flex flex-col items-center gap-5">
        <div
          className="w-24 h-24 rounded-3xl flex items-center justify-center border"
          style={{
            background: 'rgba(255,255,255,0.15)',
            backdropFilter: 'blur(8px)',
            borderColor: 'rgba(255,255,255,0.25)',
          }}
        >
          <Icon className="w-12 h-12 text-white" />
        </div>
        <div className="text-center">
          <p className="text-white font-bold text-xl mb-1">{step.name}</p>
          <div className="flex items-center justify-center gap-1.5">
            <SparklesIcon className="w-3.5 h-3.5 text-white/60" />
            <span className="text-white/60 text-sm">AI 智能处理</span>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap justify-center">
          {step.tags.map((tag) => (
            <span
              key={tag}
              className="px-2.5 py-1 text-[11px] font-medium rounded-full"
              style={{ background: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.9)' }}
            >
              {tag}
            </span>
          ))}
        </div>
      </div>

      <div
        className="absolute bottom-0 left-0 right-0 h-1"
        style={{ background: 'rgba(255,255,255,0.15)' }}
      />
    </div>
  );
};

const StepCard: React.FC<{ step: StepData; index: number; visible: boolean }> = ({
  step,
  index,
  visible,
}) => {
  const isReverse = index % 2 === 1;

  return (
    <div
      className={`flex flex-col md:flex-row gap-12 lg:gap-20 items-center transition-all duration-700 ease-out ${
        isReverse ? 'md:flex-row-reverse' : ''
      } ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 mb-5">
          <span
            className="text-xs font-bold px-3 py-1.5 rounded-full border"
            style={{
              color: step.lightText,
              borderColor: step.accentColor + '40',
              background: step.lightBg,
            }}
          >
            步骤 {step.id}
          </span>
          <div
            className="h-px flex-1"
            style={{
              background: `linear-gradient(to right, ${step.accentColor}40, transparent)`,
            }}
          />
        </div>

        <h3 className="text-3xl md:text-4xl font-black text-slate-900 mb-5 tracking-tight">
          {step.name}
        </h3>
        <p className="text-lg text-slate-500 leading-relaxed mb-8">{step.description}</p>

        <div className="space-y-3.5">
          {step.features.map((feature, fi) => (
            <div key={fi} className="flex items-start gap-3">
              <div
                className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                style={{ background: step.accentColor + '18' }}
              >
                <div
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ background: step.accentColor }}
                />
              </div>
              <span className="text-slate-600 text-base leading-relaxed">{feature}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex-1 w-full md:max-w-sm lg:max-w-md">
        <StepVisual step={step} />
      </div>
    </div>
  );
};

const StepsSection: React.FC = () => {
  const [visibleSteps, setVisibleSteps] = useState<boolean[]>(STEPS.map(() => false));
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observers = stepRefs.current.map((ref, i) => {
      if (!ref) return null;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              setVisibleSteps((prev) => {
                const next = [...prev];
                next[i] = true;
                return next;
              });
            }, i * 80);
          }
        },
        { threshold: 0.15 }
      );
      observer.observe(ref);
      return observer;
    });
    return () => observers.forEach((obs) => obs?.disconnect());
  }, []);

  return (
    <section id="steps" className="py-28 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-20">
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border mb-6"
            style={{ background: '#eff6ff', borderColor: '#bfdbfe' }}
          >
            <SparklesIcon className="w-3.5 h-3.5 text-blue-600" />
            <span className="text-sm font-semibold text-blue-600">标书创建流程</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-6 tracking-tight">
            5 步完成一份专业标书
          </h2>
          <p className="text-xl text-slate-500 max-w-2xl mx-auto leading-relaxed">
            从上传招标文件到导出完整标书，全程 AI 辅助，每一步都有智能支撑
          </p>
        </div>

        <div className="space-y-28">
          {STEPS.map((step, i) => (
            <div
              key={step.id}
              ref={(el) => { stepRefs.current[i] = el; }}
            >
              <StepCard step={step} index={i} visible={visibleSteps[i]} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StepsSection;
